import { useState } from 'react';
import { useAccount, useWriteContract } from 'wagmi';
import { readContract } from '@wagmi/core';
import { config } from '../config/web3';
import { toast } from 'react-toastify';
import { FaMusic, FaImage, FaUpload, FaPlus, FaTimes } from 'react-icons/fa';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { uploadToPinata, uploadMetadataToPinata } from '../utils/pinata';
import { useContentModeration, useCopyrightVerification } from '../hooks/useGenLayer';
import { ContentModerationPanel } from '../components/genlayer/ContentModerationBadge';

const MUSIC_NFT_CONTRACT = import.meta.env.VITE_MUSIC_NFT_CONTRACT || '0xF29A2DCC8877fac176C36F30d6245C4320e90841';

const GENRES = [
  'Hip Hop', 'R&B', 'Pop', 'Rock', 'Electronic', 'House', 'Techno',
  'Trap', 'Drill', 'Afrobeats', 'Reggae', 'Jazz', 'Soul', 'Funk',
  'Country', 'Latin', 'K-Pop', 'Indie', 'Alternative', 'Metal', 'Other'
];

const RELEASE_TYPES = ['Single', 'EP', 'Album', 'Track'];

export default function Upload() {
  const { isConnected } = useAccount();
  const { writeContractAsync } = useWriteContract();
  const { moderateContent, isModeratingContent, moderationResult } = useContentModeration();
  const { verifyCopyright, isVerifying } = useCopyrightVerification();
  
  const [isUploading, setIsUploading] = useState(false);
  const [customGenre, setCustomGenre] = useState('');
  const [formData, setFormData] = useState({
    trackTitle: '',
    artistName: '',
    producer: '',
    otherArtists: '',
    albumName: '',
    releaseType: 'Single',
    genre: 'Hip Hop',
    isExplicit: false,
    samples: [''],
    coverArtFile: null as File | null,
    audioFile: null as File | null,
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Reset custom genre when switching away from "Other"
    if (name === 'genre' && value !== 'Other') {
      setCustomGenre('');
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>, fileType: 'coverArt' | 'audio') => {
    const file = e.target.files?.[0];
    if (file) {
      if (fileType === 'coverArt') {
        // Validate image
        if (!file.type.startsWith('image/')) {
          toast.error('Please select a valid image file');
          return;
        }
        setFormData(prev => ({ ...prev, coverArtFile: file }));
      } else {
        // Validate audio
        if (!file.type.startsWith('audio/')) {
          toast.error('Please select a valid audio file');
          return;
        }
        setFormData(prev => ({ ...prev, audioFile: file }));
      }
    }
  };

  const addSample = () => {
    setFormData(prev => ({
      ...prev,
      samples: [...prev.samples, '']
    }));
  };

  const removeSample = (index: number) => {
    setFormData(prev => ({
      ...prev,
      samples: prev.samples.filter((_, i) => i !== index)
    }));
  };

  const updateSample = (index: number, value: string) => {
    setFormData(prev => ({
      ...prev,
      samples: prev.samples.map((s, i) => i === index ? value : s)
    }));
  };


  const getAudioDuration = (file: File): Promise<number> => {
    return new Promise((resolve, reject) => {
      const audio = new Audio();
      audio.preload = 'metadata';
      
      audio.onloadedmetadata = () => {
        window.URL.revokeObjectURL(audio.src);
        resolve(audio.duration);
      };
      
      audio.onerror = () => {
        window.URL.revokeObjectURL(audio.src);
        reject(new Error('Failed to load audio metadata'));
      };
      
      audio.src = URL.createObjectURL(file);
    });
  };

  const formatDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!isConnected) {
      toast.error('Please connect your wallet');
      return;
    }

    if (!formData.trackTitle || !formData.artistName) {
      toast.error('Please fill in all required fields');
      return;
    }

    // Validate custom genre if "Other" is selected
    if (formData.genre === 'Other' && !customGenre.trim()) {
      toast.error('Please enter a custom genre');
      return;
    }

    if (!formData.coverArtFile || !formData.audioFile) {
      toast.error('Please upload both cover art and audio file');
      return;
    }

    setIsUploading(true);

    try {
      // Generate track ID
      const trackId = `track_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
      const finalGenre = formData.genre === 'Other' ? customGenre.trim() : formData.genre;
      
      // ====== Step 1: Upload files to IPFS ======
      toast.info('📁 Extracting audio duration...');
      
      // Get audio duration
      const durationInSeconds = await getAudioDuration(formData.audioFile);
      toast.success('✅ Audio duration extracted');
      
      let coverArtURI: string;
      let audioURI: string;
      
      toast.info('☁️ Uploading cover art to IPFS...');
      try {
        coverArtURI = await uploadToPinata(formData.coverArtFile);
        toast.success('✅ Cover art uploaded to IPFS');
        console.log('Cover Art URI:', coverArtURI);
      } catch (ipfsError) {
        console.error('Cover art upload failed:', ipfsError);
        throw new Error(`Cover art upload failed: ${ipfsError instanceof Error ? ipfsError.message : 'Unknown error'}`);
      }
      
      toast.info('☁️ Uploading audio to IPFS...');
      try {
        audioURI = await uploadToPinata(formData.audioFile);
        toast.success('✅ Audio uploaded to IPFS');
        console.log('Audio URI:', audioURI);
      } catch (ipfsError) {
        console.error('Audio upload failed:', ipfsError);
        throw new Error(`Audio upload failed: ${ipfsError instanceof Error ? ipfsError.message : 'Unknown error'}`);
      }
      
      // Create metadata JSON (NFT standard format)
      const metadata = {
        name: formData.trackTitle,
        description: `${formData.trackTitle} by ${formData.artistName}${formData.producer ? ` (Produced by ${formData.producer})` : ''}${formData.otherArtists ? ` feat. ${formData.otherArtists}` : ''}`,
        image: coverArtURI,
        animation_url: audioURI,
        audio_url: audioURI,
        artist: formData.artistName,
        producer: formData.producer || '',
        otherArtists: formData.otherArtists || '',
        genre: finalGenre,
        duration: formatDuration(durationInSeconds),
        playCount: 0,
        attributes: [
          { trait_type: 'Artist', value: formData.artistName },
          { trait_type: 'Producer', value: formData.producer || 'N/A' },
          { trait_type: 'Featured Artists', value: formData.otherArtists || 'N/A' },
          { trait_type: 'Album', value: formData.albumName || 'N/A' },
          { trait_type: 'Release Type', value: formData.releaseType },
          { trait_type: 'Genre', value: finalGenre },
          { trait_type: 'Duration', value: formatDuration(durationInSeconds) },
        ]
      };
      
      // Upload metadata to IPFS
      toast.info('☁️ Uploading metadata to IPFS...');
      let tokenURI: string;
      try {
        tokenURI = await uploadMetadataToPinata(metadata);
        toast.success('✅ Metadata uploaded to IPFS');
        console.log('Token URI:', tokenURI);
      } catch (ipfsError) {
        console.error('Metadata upload failed:', ipfsError);
        throw new Error(`Metadata upload failed: ${ipfsError instanceof Error ? ipfsError.message : 'Unknown error'}`);
      }
      
      // ====== Step 2: Mint NFT ======
      toast.info('🔐 Please sign transaction to mint NFT...');
      
      // Filter out empty samples
      const samples = formData.samples.filter(s => s.trim() !== '');
      
      console.log('Minting with params:', {
        trackId,
        trackTitle: formData.trackTitle,
        artistName: formData.artistName,
        albumName: formData.albumName || '',
        releaseType: formData.releaseType,
        genre: finalGenre,
        samples,
        coverArtURI,
        audioURI,
        duration: BigInt(Math.floor(durationInSeconds)),
        isExplicit: formData.isExplicit,
        tokenURI,
        value: BigInt(1000000000000000)
      });
      
      // Mint NFT
      let txHash: string;
      try {
        txHash = await writeContractAsync({
          address: MUSIC_NFT_CONTRACT as `0x${string}`,
          abi: [
            {
              name: 'mintMusic',
              type: 'function',
              stateMutability: 'payable',
              inputs: [
                { name: 'trackId', type: 'string' },
                { name: 'trackTitle', type: 'string' },
                { name: 'artistName', type: 'string' },
                { name: 'albumName', type: 'string' },
                { name: 'releaseType', type: 'string' },
                { name: 'genre', type: 'string' },
                { name: 'samples', type: 'string[]' },
                { name: 'coverArtURI', type: 'string' },
                { name: 'audioURI', type: 'string' },
                { name: 'duration', type: 'uint256' },
                { name: 'isExplicit', type: 'bool' },
                { name: 'tokenURI', type: 'string' }
              ],
              outputs: [{ name: '', type: 'uint256' }]
            }
          ],
          functionName: 'mintMusic',
          args: [
            trackId,
            formData.trackTitle,
            formData.artistName,
            formData.albumName || '',
            formData.releaseType,
            finalGenre,
            samples,
            coverArtURI,
            audioURI,
            BigInt(Math.floor(durationInSeconds)),
            formData.isExplicit,
            tokenURI
          ],
          value: BigInt(1000000000000000), // 0.001 ETH mint fee
        });
        console.log('Transaction successful:', txHash);
      } catch (contractError) {
        console.error('Contract call failed:', contractError);
        throw new Error(`Contract minting failed: ${contractError instanceof Error ? contractError.message : 'Unknown error'}`);
      }

      toast.success('🎉 Track uploaded and NFT minted successfully!');
      toast.info(`📝 Transaction hash: ${txHash}`);
      
      // ====== Step 3: Run GenLayer AI Moderation (Background) ======
      // This runs after minting and doesn't block the user
      setTimeout(async () => {
        try {
          toast.info('🧠 Running AI content moderation via GenLayer...');
          
          const modResult = await moderateContent({
            trackId,
            trackTitle: formData.trackTitle,
            artistName: formData.artistName,
            albumName: formData.albumName || '',
            genre: finalGenre,
            description: `${formData.trackTitle} by ${formData.artistName}`,
            isExplicit: formData.isExplicit,
          });

          if (modResult.status === 'FLAGGED') {
            toast.warning(`⚠️ Content flagged by AI: ${modResult.reason || 'Policy violation'}`);
          } else if (modResult.status === 'APPROVED') {
            toast.success('✅ AI content moderation passed!');
            
            // Try to sync to blockchain
            try {
              const isEnforced = await readContract(config, {
                address: MUSIC_NFT_CONTRACT as `0x${string}`,
                abi: [{
                  name: 'isModerationEnforced',
                  type: 'function',
                  stateMutability: 'view',
                  inputs: [],
                  outputs: [{ name: '', type: 'bool' }]
                }],
                functionName: 'isModerationEnforced',
                args: [],
              });
              
              if (!isEnforced) {
                toast.info('ℹ️ Moderation enforcement is disabled on contract');
              }
            } catch (err) {
              console.warn('Failed to check enforcement status:', err);
            }
          }
        } catch (modError) {
          console.warn('GenLayer moderation skipped (not configured):', modError);
        }
      }, 1000); // Start after 1 second

      // ====== Step 4: Run GenLayer Copyright Verification (Background) ======
      setTimeout(async () => {
        try {
          const copyResult = await verifyCopyright({
            trackId,
            trackTitle: formData.trackTitle,
            artistName: formData.artistName,
            claimedOriginal: true,
            sampleSources: formData.samples.filter(s => s.trim()).join(', '),
          });

          if (copyResult.status === 'FLAGGED') {
            toast.warning(`🚫 Copyright issue detected: ${copyResult.details || 'Potential infringement'}`);
          }
        } catch (copyError) {
          console.warn('GenLayer copyright check skipped:', copyError);
        }
      }, 2000); // Start after 2 seconds
      
      // Reset form
      setFormData({
        trackTitle: '',
        artistName: '',
        producer: '',
        otherArtists: '',
        albumName: '',
        releaseType: 'Single',
        genre: 'Hip Hop',
        isExplicit: false,
        samples: [''],
        coverArtFile: null,
        audioFile: null,
      });
      setCustomGenre('');
      
    } catch (error) {
      console.error('Upload error:', error);
      
      // Provide specific error messages based on what failed
      if (error instanceof Error) {
        const errorMessage = error.message.toLowerCase();
        
        if (errorMessage.includes('user rejected') || errorMessage.includes('user denied')) {
          toast.error('❌ Transaction rejected by user');
        } else if (errorMessage.includes('insufficient funds')) {
          toast.error('❌ Insufficient funds for transaction');
        } else if (errorMessage.includes('ipfs') || errorMessage.includes('pinata')) {
          toast.error('❌ Failed to upload files to IPFS');
        } else if (errorMessage.includes('network') || errorMessage.includes('rpc')) {
          toast.error('❌ Network error - please check your connection');
        } else if (errorMessage.includes('contract') || errorMessage.includes('mint')) {
          toast.error('❌ Contract error - transaction failed');
        } else {
          toast.error(`❌ Upload failed: ${error.message}`);
        }
      } else {
        toast.error('❌ Failed to upload music - unknown error');
      }
    } finally {
      setIsUploading(false);
    }
  };

  if (!isConnected) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] space-y-4">
        <FaMusic className="text-6xl text-gray-600" />
        <h2 className="text-2xl font-bold">Connect Wallet to Upload</h2>
        <p className="text-gray-400 text-center max-w-md">
          Connect your wallet to start uploading your music to BlockMusic
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Upload Your Music</h1>
        <p className="text-gray-400 mt-2">
          Distribute your music as NFTs and earn from every play
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Information */}
        <div className="bg-gray-800 rounded-lg p-6 space-y-4">
          <h2 className="text-xl font-semibold mb-4">Basic Information</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">
                Track Title <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="trackTitle"
                value={formData.trackTitle}
                onChange={handleInputChange}
                required
                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 focus:outline-none focus:border-purple-500"
                placeholder="Enter track title"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">
                Artist Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="artistName"
                value={formData.artistName}
                onChange={handleInputChange}
                required
                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 focus:outline-none focus:border-purple-500"
                placeholder="Enter artist name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">
                Producer (Optional)
              </label>
              <input
                type="text"
                name="producer"
                value={formData.producer}
                onChange={handleInputChange}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 focus:outline-none focus:border-purple-500"
                placeholder="Enter producer name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">
                Other Artists (Optional)
              </label>
              <input
                type="text"
                name="otherArtists"
                value={formData.otherArtists}
                onChange={handleInputChange}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 focus:outline-none focus:border-purple-500"
                placeholder="Enter featured artists (comma separated)"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">
                Album Name (Optional)
              </label>
              <input
                type="text"
                name="albumName"
                value={formData.albumName}
                onChange={handleInputChange}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 focus:outline-none focus:border-purple-500"
                placeholder="Enter album name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">
                Release Type <span className="text-red-500">*</span>
              </label>
              <select
                name="releaseType"
                value={formData.releaseType}
                onChange={handleInputChange}
                required
                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 focus:outline-none focus:border-purple-500"
              >
                {RELEASE_TYPES.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">
                Genre <span className="text-red-500">*</span>
              </label>
              <select
                name="genre"
                value={formData.genre}
                onChange={handleInputChange}
                required
                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 focus:outline-none focus:border-purple-500"
              >
                {GENRES.map(genre => (
                  <option key={genre} value={genre}>{genre}</option>
                ))}
              </select>
              {formData.genre === 'Other' && (
                <input
                  type="text"
                  value={customGenre}
                  onChange={(e) => setCustomGenre(e.target.value)}
                  placeholder="Enter custom genre"
                  className="mt-2 w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 focus:outline-none focus:border-purple-500"
                  required
                />
              )}
            </div>

          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              name="isExplicit"
              checked={formData.isExplicit}
              onChange={handleInputChange}
              className="w-4 h-4 text-purple-600 bg-gray-700 border-gray-600 rounded focus:ring-purple-500"
            />
            <label className="ml-2 text-sm">
              Explicit Content
            </label>
          </div>
        </div>

        {/* Samples Used */}
        <div className="bg-gray-800 rounded-lg p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold">Samples Used (Optional)</h2>
            <button
              type="button"
              onClick={addSample}
              className="flex items-center gap-2 bg-purple-600 hover:bg-purple-700 px-3 py-1 rounded text-sm"
            >
              <FaPlus /> Add Sample
            </button>
          </div>
          
          {formData.samples.map((sample, index) => (
            <div key={index} className="flex gap-2">
              <input
                type="text"
                value={sample}
                onChange={(e) => updateSample(index, e.target.value)}
                className="flex-1 bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 focus:outline-none focus:border-purple-500"
                placeholder="Sample name or description"
              />
              {formData.samples.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeSample(index)}
                  className="bg-red-600 hover:bg-red-700 px-3 py-2 rounded"
                >
                  <FaTimes />
                </button>
              )}
            </div>
          ))}
        </div>

        {/* File Uploads */}
        <div className="bg-gray-800 rounded-lg p-6 space-y-4">
          <h2 className="text-xl font-semibold mb-4">Media Files</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Cover Art Upload */}
            <div>
              <label className="block text-sm font-medium mb-2">
                Cover Art <span className="text-red-500">*</span>
              </label>
              <div className="border-2 border-dashed border-gray-600 rounded-lg p-6 text-center hover:border-purple-500 transition-colors">
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleFileChange(e, 'coverArt')}
                  className="hidden"
                  id="coverArt"
                  required
                />
                <label htmlFor="coverArt" className="cursor-pointer">
                  <FaImage className="mx-auto text-4xl text-gray-500 mb-2" />
                  <p className="text-sm text-gray-400">
                    {formData.coverArtFile ? formData.coverArtFile.name : 'Click to upload cover art'}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">PNG, JPG, GIF up to 10MB</p>
                </label>
              </div>
            </div>

            {/* Audio Upload */}
            <div>
              <label className="block text-sm font-medium mb-2">
                Audio File <span className="text-red-500">*</span>
              </label>
              <div className="border-2 border-dashed border-gray-600 rounded-lg p-6 text-center hover:border-purple-500 transition-colors">
                <input
                  type="file"
                  accept="audio/*"
                  onChange={(e) => handleFileChange(e, 'audio')}
                  className="hidden"
                  id="audioFile"
                  required
                />
                <label htmlFor="audioFile" className="cursor-pointer">
                  <FaMusic className="mx-auto text-4xl text-gray-500 mb-2" />
                  <p className="text-sm text-gray-400">
                    {formData.audioFile ? formData.audioFile.name : 'Click to upload audio'}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">MP3, WAV, FLAC up to 100MB</p>
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* GenLayer AI Moderation Status */}
        <ContentModerationPanel
          result={moderationResult}
          isLoading={isModeratingContent || isVerifying}
          onRetry={() => {
            // User can retry moderation
            toast.info('Retrying AI moderation...');
          }}
        />

        {/* Submit Button */}
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={isUploading}
            className="flex items-center gap-2 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white px-8 py-3 rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isUploading ? (
              <>
                <LoadingSpinner size="sm" />
                Uploading...
              </>
            ) : (
              <>
                <FaUpload />
                Upload Music
              </>
            )}
          </button>
        </div>
      </form>

      {/* Info Box */}
      <div className="bg-purple-900/20 border border-purple-500/50 rounded-lg p-4 text-sm">
        <h3 className="font-semibold mb-2">📝 Upload Guidelines:</h3>
        <ul className="space-y-1 text-gray-400">
          <li>• You own all rights to the music you upload</li>
          <li>• Files are stored on IPFS for decentralization</li>
          <li>• NFT represents your ownership - not for sale</li>
          <li>• You earn from every play of your music</li>
          <li>• 100% of streaming revenue goes to you</li>
          <li>• Users stream via subscription - no NFT purchase needed</li>
          <li>• 🧠 <strong>AI-powered moderation</strong> via GenLayer checks content before publishing</li>
          <li>• 🔍 <strong>Copyright verification</strong> uses on-chain AI to protect creators</li>
        </ul>
      </div>
    </div>
  );
}
